import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../../../provider/ToastProvider";
import * as userActions from "../../../redux/reducers/userReducer";
import { userState } from "../../../redux/reducers/userReducer";
import { createBusinessDomain } from "../../../helpers";
import axiosInstance from "../../../utils/axiosInstance";
import { CURRENT_ENV, getUrlForUsers } from "../../../utils/confighelpers";
import { SettingsCard, SecondaryButton, Toggle } from "../../settings/accountSettings/shared";
import { FcModal, Icon } from "../schedule/ui";

/**
 * Free trial class for newcomers (no account, no password): the club's open schedule at <club>.<root>/trial
 * (modals/trial). The club turns it on with users.allowTrialBooking (PUT users/updateProfile; the backend refuses it
 * when the club has no "free trial credit" product).
 *  - TrialBookingCard: Account settings > Club, the on/off switch + the link.
 *  - TrialLinkModal:   Members > Add members > "Trial class link", the link to share (and the switch when it's off).
 */

/** Member sign-up page on the club's address (used for the Club link card). */
export const trialLinkFor = (userData) => {
  const link = userData?.myBookingLink;
  if (!link) return "";
  if (CURRENT_ENV === "local" || window.location.hostname === "localhost") {
    return `${window.location.origin}/members/signup/${link}`;
  }
  return `${createBusinessDomain(link)}members/signup`;
};

/** The free trial class page (open schedule) on the club's address. */
export const trialPageLinkFor = (userData) => {
  const link = userData?.myBookingLink;
  if (!link) return "";
  if (CURRENT_ENV === "local" || window.location.hostname === "localhost") {
    return `${window.location.origin}/trial?club=${encodeURIComponent(link)}`;
  }
  return `${createBusinessDomain(link)}trial`;
};

/** The club's "Let newcomers book a free trial class" switch. */
export function useTrialBookingSetting() {
  const dispatch = useDispatch();
  const { addSuccessToast, addErrorToast } = useToast();
  const userData = useSelector(userState).userData || {};
  const [saving, setSaving] = React.useState(false);

  const setEnabled = async (on) => {
    setSaving(true);
    try {
      const res = await axiosInstance({ method: "put", url: `${getUrlForUsers()}updateProfile`, data: { allowTrialBooking: !!on } });
      if (res?.status === 200 && res?.data?.statusCode === 200) {
        const data = res.data.data || {};
        dispatch(userActions.setUserData({ appUrl: userData.appUrl, ...data, allowTrialBooking: !!on }));
        addSuccessToast(on ? "Free trial booking is on" : "Free trial booking is off");
        return true;
      }
      addErrorToast(typeof res?.data?.message === "string" ? res.data.message : "Couldn't save. Please try again.");
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setSaving(false);
    }
    return false;
  };

  return { enabled: userData.allowTrialBooking === true, saving, setEnabled, userData };
}

const shareText = (club, link) => `Try a free class at ${club}! Pick a time and book it here: ${link}`;

/**
 * The link box with Copy link, plus Share on WhatsApp / Copy text / Open page.
 * Used by the free trial card and the Club link card (Account settings > Club).
 *  - text:  the message for WhatsApp / Copy text (with the link in it)
 *  - guard: optional wrapper for every action, e.g. the payments check: guard={(go) => payments.whenReady(go)}
 */
export function ShareLinkBox({ link, text, guard = (go) => go() }) {
  const { addSuccessToast, addErrorToast } = useToast();
  const copy = (value, done) =>
    guard(async () => {
      try {
        await navigator.clipboard.writeText(value);
        addSuccessToast(done);
      } catch (e) {
        addErrorToast("Couldn't copy. Select the text and copy it yourself.");
      }
    });
  // usePaymentsGate().whenReady runs the action straight away (still inside the click) when payments are set up,
  // so the new tab isn't blocked.
  const open = (url) => guard(() => window.open(url, "_blank", "noopener"));
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 rounded-2xl border border-[#E3D0F7] bg-[#F3E9FF] p-2 pl-4">
        <span className="min-w-0 flex-1 truncate font-mono text-sm font-bold text-brown-coffee" title={link}>{link}</span>
        <button type="button" onClick={() => copy(link, "Link copied")} className="shrink-0 rounded-xl bg-white px-4 py-2 text-sm font-extrabold text-indigo-bz shadow-sm hover:bg-[#FBF8FE]">
          Copy link
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        <SecondaryButton onClick={() => open(`https://wa.me/?text=${encodeURIComponent(text)}`)}>
          Share on WhatsApp
        </SecondaryButton>
        <SecondaryButton onClick={() => copy(text, "Text copied")}>Copy text</SecondaryButton>
        <SecondaryButton onClick={() => open(link)}>
          <Icon className="h-4 w-4" d={<><path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" /></>} />
          Open page
        </SecondaryButton>
      </div>
    </div>
  );
}

const HOW_IT_WORKS = [
  "Newcomers pick a class on your open schedule and fill in their name, mobile, email, age and gender.",
  "They confirm with a code we text them. No password, no payment.",
  "They get 1 free class, a text with the details and a link to change or cancel (up to 3 hours before).",
  "They show up in My Members as Guest. Add them to your members when they want to join.",
];

/** Account settings > Club: switch + link. */
export function TrialBookingCard() {
  const { enabled, saving, setEnabled, userData } = useTrialBookingSetting();
  const club = userData?.myBusinessProfile?.businessName || "our club";
  const link = trialPageLinkFor(userData);
  return (
    <SettingsCard title="Free trial class" subtitle="Let newcomers book 1 free class without creating an account.">
      <div className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-[#FBF9FC] px-4 py-3.5">
        <div className="min-w-0">
          <div className="text-[15px] font-extrabold text-gray-900">Let newcomers book a free trial class</div>
          <div className="text-xs text-gray-500">{enabled ? "On: your trial page is live." : "Off: the trial page shows that you're not taking trial bookings."}</div>
        </div>
        <Toggle checked={enabled} disabled={saving || !userData?.myBookingLink} onChange={setEnabled} label="Let newcomers book a free trial class" />
      </div>
      {!userData?.myBookingLink ? (
        <p className="mt-3 text-sm font-semibold text-red-700">Save your club link first.</p>
      ) : enabled ? (
        <div className="mt-4 flex flex-col gap-2">
          <div className="text-sm font-extrabold text-gray-900">Your trial page</div>
          <ShareLinkBox link={link} text={shareText(club, link)} />
          <p className="text-xs text-gray-500">Put this link on your website ("Book a free trial class"), Instagram bio or a QR code on a flyer.</p>
        </div>
      ) : null}
      <ul className="mt-4 flex flex-col gap-1.5 text-sm text-gray-600">
        {HOW_IT_WORKS.map((t, i) => (
          <li key={i} className="flex gap-2">
            <span className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#F3E9FF] text-[10px] font-black text-indigo-bz">{i + 1}</span>
            {t}
          </li>
        ))}
      </ul>
    </SettingsCard>
  );
}

/** Members > Add members > "Trial class link". */
export default function TrialLinkModal({ open, onClose }) {
  const { enabled, saving, setEnabled, userData } = useTrialBookingSetting();
  const club = userData?.myBusinessProfile?.businessName || "our club";
  const link = trialPageLinkFor(userData);

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="TRIAL CLASS"
      title={enabled ? "Share your free trial link" : "Turn on free trial classes"}
      subtitle="Newcomers pick a class and book 1 free class. No account, no payment."
      primaryLabel={enabled ? "Done" : "Turn on"}
      busy={saving}
      primaryDisabled={!link}
      onPrimary={async () => {
        if (enabled) onClose();
        else await setEnabled(true);
      }}
    >
      {!link ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
          Your club doesn't have a web address yet. Add it under Settings › Club, then come back here.
        </div>
      ) : enabled ? (
        <>
          <ShareLinkBox link={link} text={shareText(club, link)} />
          <p className="text-[13px] text-gray-500">
            Post it on your website, Instagram or a flyer. Trial guests show up in My Members as Guest. You can turn this off under Settings › Club.
          </p>
        </>
      ) : (
        <ul className="flex flex-col gap-2 text-sm text-gray-700">
          {HOW_IT_WORKS.map((t, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="mt-[2px] flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F3E9FF] text-[11px] font-black text-indigo-bz">{i + 1}</span>
              {t}
            </li>
          ))}
        </ul>
      )}
    </FcModal>
  );
}
